import { Check, Copy, Gift, PaperPlaneTilt, UserPlus, UsersThree } from '@phosphor-icons/react';
import { useState } from 'react';

import { useClaimReferralRewardMutation, useGetReferralsQuery } from '@entities/referral';
import { apiErrorMessage } from '@shared/api/baseApi';
import { haptics, openTelegramLink } from '@shared/lib/telegram';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { EmptyState } from '@shared/ui/EmptyState';
import { Screen } from '@shared/ui/Screen';
import { Spinner } from '@shared/ui/Spinner';
import styles from './InvitePage.module.css';

function claimStatus(status: string): string {
  if (status === 'WAITING_STOCK') return 'Waiting for availability';
  if (status === 'FULFILLED') return 'Delivered';
  if (status === 'REJECTED') return 'Not approved';
  return 'Pending approval';
}

export function InvitePage() {
  const { data, isLoading } = useGetReferralsQuery(undefined, {
    pollingInterval: 20_000,
    refetchOnFocus: true,
  });
  const [claim, { isLoading: claiming }] = useClaimReferralRewardMutation();
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isLoading || !data) {
    return <Screen title="Invite & Earn"><Spinner label="Loading referrals" /></Screen>;
  }

  const remaining = Math.max(0, data.target - data.progress);
  const share = () => {
    const text = 'Join me on YeneShop. Your first completed purchase helps me unlock a reward!';
    openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(data.inviteLink)}&text=${encodeURIComponent(text)}`);
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.inviteLink);
      setCopied(true);
      haptics.notify('success');
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setError('Could not copy the link. Please try again.');
    }
  };
  const submitClaim = async () => {
    setError(null);
    try {
      await claim().unwrap();
      haptics.notify('success');
    } catch (cause) {
      setError(apiErrorMessage(cause, 'Could not submit your reward claim.'));
    }
  };

  return (
    <Screen title="Invite & Earn">
      <Card className={styles.hero}>
        <span className={styles.gift}><Gift size={30} weight="fill" /></span>
        <p className={styles.eyebrow}>Invite & Earn</p>
        <h2>Get {data.rewardProduct?.name ?? 'a premium reward'} FREE</h2>
        <p className={styles.subtitle}>Invite 5 friends who complete their first purchase.</p>

        {data.rewardProduct ? (
          <div className={styles.reward}>
            <img src={data.rewardProduct.logoUrl} alt="" />
            <span>{data.rewardProduct.name}</span>
          </div>
        ) : null}

        <p className={styles.progressNumber}>{data.progress} / {data.target}</p>
        <div className={styles.dots} aria-label={`${data.progress} of ${data.target} successful referrals`}>
          {Array.from({ length: data.target }, (_, index) => (
            <span key={index} className={index < data.progress ? styles.dotDone : styles.dot} />
          ))}
        </div>
        <p className={styles.remaining}>
          {remaining === 0 ? 'Your reward is unlocked' : `${remaining} more friend${remaining === 1 ? '' : 's'} to unlock your reward`}
        </p>

        {data.activeClaim ? (
          <div className={styles.claimState}>
            <strong>{claimStatus(data.activeClaim.status)}</strong>
            <span>
              {data.activeClaim.status === 'WAITING_STOCK'
                ? 'This reward is currently out of stock. You can claim it when stock becomes available.'
                : 'Your request has been sent for admin approval.'}
            </span>
          </div>
        ) : data.progress === data.target && !data.rewardProduct ? (
          <Button fullWidth disabled>Reward coming soon</Button>
        ) : data.canClaim ? (
          <Button fullWidth loading={claiming} onClick={() => void submitClaim()}>
            <Gift size={19} weight="fill" /> Claim {data.rewardProduct?.name ?? 'Reward'}
          </Button>
        ) : (
          <Button fullWidth onClick={share}>
            <PaperPlaneTilt size={19} weight="fill" /> Invite Friends
          </Button>
        )}

        <Button fullWidth variant="secondary" onClick={() => void copy()}>
          {copied ? <Check size={18} weight="bold" /> : <Copy size={18} weight="bold" />}
          {copied ? 'Copied' : 'Copy Link'}
        </Button>
        {error ? <p className={styles.error}>{error}</p> : null}
        <p className={styles.note}>Friends count after completing their first paid order.</p>
      </Card>

      <section className={styles.section}>
        <div className={styles.sectionTitle}><UsersThree size={20} weight="fill" /><h2>Your friends</h2></div>
        {data.referrals.length === 0 ? (
          <EmptyState icon={UserPlus} title="No invited friends yet" description="Share your link to get started." />
        ) : (
          <div className={styles.list}>
            {data.referrals.map((referral) => (
              <Card key={referral.id} className={styles.friend}>
                <span className={styles.avatar}>{(referral.friend.firstName ?? referral.friend.username ?? '?')[0]?.toUpperCase()}</span>
                <span className={styles.friendName}>
                  <strong>{referral.friend.firstName ?? 'Telegram user'}</strong>
                  <small>{referral.friend.username ? `@${referral.friend.username}` : 'No username'}</small>
                </span>
                <span className={referral.status === 'SUCCESSFUL' ? styles.success : styles.pending}>
                  {referral.status === 'SUCCESSFUL' ? 'Successful' : 'Waiting for purchase'}
                </span>
              </Card>
            ))}
          </div>
        )}
      </section>

      {data.claims.length > 0 ? (
        <section className={styles.section}>
          <div className={styles.sectionTitle}><Gift size={20} weight="fill" /><h2>Reward history</h2></div>
          <div className={styles.list}>
            {data.claims.map((entry) => (
              <Card key={entry.id} className={styles.history}>
                <span><strong>{entry.productName}</strong><small>{new Date(entry.createdAt).toLocaleDateString()}</small></span>
                <span className={entry.status === 'FULFILLED' ? styles.success : entry.status === 'REJECTED' ? styles.rejected : styles.pending}>
                  {claimStatus(entry.status)}
                </span>
              </Card>
            ))}
          </div>
        </section>
      ) : null}
    </Screen>
  );
}
